/**
 * QA Panel — Jackpot mock controller.
 *
 * Lets QA freeze the real jackpot polling and inject synthetic value updates
 * to trigger the odometer animations on demand. Works entirely through the
 * jackpotsStore's existing public API — zero changes to production jackpot
 * code, no network interception, no bypassing of the animation pipeline.
 *
 * How it works:
 *   - "Mock mode ON"  → calls jackpotsStore.stopPolling() so the next real
 *                       fetch can't overwrite our synthetic values.
 *   - "Mock mode OFF" → reads apiHost + currency from appStore and calls
 *                       startPolling() to resume the normal fetch loop.
 *   - "Trigger"       → reads the current jackpotsFromAPI map, multiplies
 *                       every level's currentValue by (1 + percent/100),
 *                       and pushes the mutated map back via
 *                       setJackpotsFromAPI(). The store's existing diff
 *                       logic notices the changed targets and fires the
 *                       odometer animation.
 *   - Successive triggers compound on the previous values, creating a
 *     believable upward (or downward) staircase.
 *   - Negative percentages let QA simulate jackpot resets after a win.
 *
 * Note on store access: we go through the features/jackpots public API
 * (`useJackpotsStore`) so the eslint-plugin-boundaries rule isn't violated.
 */

import { JACKPOT_NAMES } from '@repo/common';
import { hasJackpotStatistics, useJackpotsStore, useJackpotStatisticsStore } from '../../jackpots';
import { useAppStore } from '../../../store/app/appStore';
import type IJackpotStat from '../../../types/models/IJackpotStat';
import type { IJackpotCurrencyValue, IJackpotLevelStat } from '../../../types/models/IJackpotStat';
import { buildJackpotWinsMock } from './jackpotWinsMockFactory';

/**
 * Hard-coded backend convention for jackpot level IDs. Documented in the
 * jackpots feature store; mirrored here so the popup can show human-readable
 * labels without reaching into feature internals.
 */
const LEVEL_NAMES: Record<number, string> = {
    1: 'GRAND',
    2: 'MAJOR',
    3: 'MINOR',
    4: 'MINI',
};

function levelNameFor(levelId: number): string {
    return LEVEL_NAMES[levelId] ?? 'LEVEL ' + levelId;
}

/** `totalWins` written onto the synthetic InstantLink level — a plausible lifetime counter. */
const SYNTHETIC_INSTANT_TOTAL_WINS = 3;

/** One editable level row as surfaced to the popup. */
export interface JackpotLevelSnapshot {
    levelId: number;
    levelName: string;
    /** Value in the jackpot's active display currency, or 0 if missing. */
    value: number;
}

/** Generation parameters for one jackpot's mocked win statistics. Round-trips through presets. */
export interface JackpotWinsMockEntry {
    jackpotName: string;
    winsPerLevel: number;
    includeZeroWinLevel: boolean;
}

/** One jackpot's full editable shape as surfaced to the popup. */
export interface JackpotSnapshot {
    name: string;
    currency: string;
    levels: JackpotLevelSnapshot[];
    /**
     * Whether this jackpot has a statistics drawer. The per-level VALUE editor lists every
     * jackpot; the Statistics mock picker lists only these, because generating win history for a
     * single-progressive jackpot (Burning Hot, Rise of Ra, ...) produces data nothing can open.
     */
    hasStatistics: boolean;
}

export interface JackpotMockState {
    /** Mock mode active — real polling is stopped. */
    mockEnabled: boolean;
    /** Auto-trigger interval is running. */
    autoActive: boolean;
    /** Current auto-trigger interval in seconds (last value used / next start). */
    autoIntervalSec: number;
    /** Current auto-trigger percentage (last value used / next start). */
    autoPercent: number;
    /** True once the lobby has fetched jackpot data at least once. */
    hasData: boolean;
    /** Number of jackpots currently in the store. */
    jackpotCount: number;
    /** Live snapshot of every jackpot + its levels (sorted by name). */
    jackpots: JackpotSnapshot[];
    /**
     * InstantLink instance status. The InstantLink stat is appended to the
     * poll response by a separate backend that exists only in production, so
     * on QA/dev envs it's simply 'absent' until QA injects a synthetic one.
     */
    instantStatus: 'live' | 'mocked' | 'absent';
    /** True when any section in appStore carries jackpotName 'InstantLink'. */
    instantSectionConfigured: boolean;
    /** Current InstantLink GRAND value in the active display currency (0 when absent). */
    instantValue: number;
    /**
     * Win-statistics mocking is on — the statistics drawer serves generated rows and the real
     * `/api/jackpot/win/stats` query is disabled.
     */
    winsMockEnabled: boolean;
    /** Generation parameters of every jackpot that currently has mocked win statistics. */
    winsMocks: JackpotWinsMockEntry[];
}

/**
 * Multiply every currency value in every level of every jackpot by `factor`.
 * Returns a new Map — does NOT mutate the input. We don't filter by the
 * lobby's active currency because the store re-derives currency on every
 * read; updating all currencies keeps the data consistent regardless of
 * which currency the lobby switches to mid-test.
 */
function applyPercentageDeep(
    map: Map<string, IJackpotStat>,
    percent: number
): Map<string, IJackpotStat> {
    const factor = 1 + percent / 100;
    const out = new Map<string, IJackpotStat>();
    map.forEach((stat, name) => {
        const newLevelStats = (stat.levelStats || []).map((level) => ({
            ...level,
            currentValue: Array.isArray(level.currentValue)
                ? level.currentValue.map((cv) => ({
                      ...cv,
                      value: (cv.value ?? 0) * factor,
                  }))
                : level.currentValue,
        }));
        out.set(name, { ...stat, levelStats: newLevelStats });
    });
    return out;
}

/**
 * Build the read-only snapshot of jackpots + levels the popup renders.
 * Sorted alphabetically by jackpot name to match the Mini Backoffice's
 * `availableGames` ordering, so the list is stable across broadcasts.
 */
function buildJackpotsSnapshot(
    map: Map<string, IJackpotStat>
): JackpotSnapshot[] {
    const out: JackpotSnapshot[] = [];
    map.forEach((stat, name) => {
        const currency = stat.currency || '';
        const levels: JackpotLevelSnapshot[] = (stat.levelStats || []).map(
            (level) => {
                const levelId = level.levelId;
                const entries = Array.isArray(level.currentValue) ? level.currentValue : [];
                const match = entries.find((cv) => cv.key === currency);
                const value = typeof match?.value === 'number' ? match.value : 0;
                return {
                    levelId,
                    levelName: levelNameFor(levelId),
                    value,
                };
            }
        );
        out.push({ name, currency, levels, hasStatistics: hasJackpotStatistics(name) });
    });
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
}

/**
 * Read the InstantLink GRAND (levelId 1) value in the stat's display
 * currency. Falls back to the first currency entry when the display
 * currency has no entry, and to 0 when the level/entries are missing.
 */
function instantGrandValue(stat: IJackpotStat | undefined): number {
    if (!stat) return 0;
    const levels = stat.levelStats || [];
    const grand = levels.find((level) => level.levelId === 1);
    if (!grand || !Array.isArray(grand.currentValue) || grand.currentValue.length === 0) return 0;
    const match = grand.currentValue.find((cv) => cv.key === stat.currency) ?? grand.currentValue[0];
    return typeof match?.value === 'number' ? match.value : 0;
}

/**
 * Build a synthetic InstantLink IJackpotStat mirroring the production shape
 * (single GRAND level — the InstantLink panel variant renders only levelId 1).
 *
 * Currency entries are written for every currency the lobby might read: the
 * active balance currency plus 'EGT' (the demo-currency convention — see
 * addEGTCurrencyJackpots in core/api/endpoints/jackpot.api.ts).
 *
 * The `games` list is sourced from the section(s) whose jackpotName is
 * 'InstantLink' so useJackpotNameByGameKey resolves game cards to this
 * instance; empty when no such section is configured (store-level mock is
 * still valid — the popup surfaces a hint in that case).
 */
function buildSyntheticInstantLinkStat(value: number): IJackpotStat {
    const appState = useAppStore.getState();
    const balanceCurrency = appState.balance?.currency ?? '';
    const currencies = Array.from(new Set([balanceCurrency, 'EGT'].filter((c) => c.length > 0)));
    const now = new Date().toISOString();
    const makeEntries = (): IJackpotCurrencyValue[] => currencies.map((key) => ({ key, value }));

    const games: string[] = [];
    for (const section of appState.sections) {
        if (section.jackpotName !== JACKPOT_NAMES.INSTANT_LINK) continue;
        for (const game of section.games || []) {
            if (!games.includes(game.gameKey)) games.push(game.gameKey);
        }
    }

    const grandLevel: IJackpotLevelStat = {
        levelId: 1,
        totalWins: SYNTHETIC_INSTANT_TOTAL_WINS,
        lastWinPlayerName: '',
        lastWinDate: now,
        maxWinPlayerName: '',
        maxWinDate: now,
        currentValue: makeEntries(),
        lastWinValue: makeEntries(),
        maxWinValue: makeEntries(),
    };

    return {
        jackpotName: JACKPOT_NAMES.INSTANT_LINK,
        currency: balanceCurrency || 'EGT',
        currencies,
        levelStats: [grandLevel],
        games,
    };
}

export class JackpotMockController {
    private mockEnabled = false;
    private instantInjected = false;
    private autoIntervalId: number | null = null;
    private autoIntervalSec = 5;
    private autoPercent = 5;
    private onChange: (() => void) | null = null;
    private storeUnsubscribe: (() => void) | null = null;
    /** Generation parameters per jackpot, so the popup and the preset can replay them. */
    private winsMocks = new Map<string, JackpotWinsMockEntry>();

    /**
     * Wire up listeners. The bridge passes a callback that fires whenever
     * any state-affecting change happens, so the popup can re-render.
     */
    init(onChange: () => void): void {
        this.onChange = onChange;
        // Watch for jackpot data arriving / changing so the "hasData" badge
        // and the jackpot count in the popup stay live.
        this.storeUnsubscribe = useJackpotsStore.subscribe(() => {
            this.onChange?.();
        });
    }

    destroy(): void {
        this.stopAuto();
        this.storeUnsubscribe?.();
        this.storeUnsubscribe = null;
        this.onChange = null;
        // If the panel is being torn down with mock mode still on, restore
        // real polling so we don't leave the lobby in a frozen state.
        if (this.mockEnabled) {
            this.disableMockMode(/* silent */ true);
        }
        // Win-statistics mocking can be on without jackpot mock mode (QA may toggle it without
        // generating anything), so it needs its own teardown — otherwise the drawer would keep
        // serving mocks after the panel is gone, with no UI left to turn it off.
        this.teardownWinsMock();
    }

    getState(): JackpotMockState {
        const jackpotsStore = useJackpotsStore.getState();
        const instantStat = jackpotsStore.jackpotsFromAPI.get(JACKPOT_NAMES.INSTANT_LINK);
        return {
            mockEnabled: this.mockEnabled,
            autoActive: this.autoIntervalId !== null,
            autoIntervalSec: this.autoIntervalSec,
            autoPercent: this.autoPercent,
            hasData: jackpotsStore.hasData,
            jackpotCount: jackpotsStore.jackpotsFromAPI.size,
            jackpots: buildJackpotsSnapshot(jackpotsStore.jackpotsFromAPI),
            instantStatus: instantStat ? (this.mockEnabled ? 'mocked' : 'live') : 'absent',
            instantSectionConfigured: useAppStore
                .getState()
                .sections.some((s) => s.jackpotName === JACKPOT_NAMES.INSTANT_LINK),
            instantValue: instantGrandValue(instantStat),
            winsMockEnabled: useJackpotStatisticsStore.getState().winsMockMode,
            winsMocks: Array.from(this.winsMocks.values()).sort((a, b) =>
                a.jackpotName.localeCompare(b.jackpotName)
            ),
        };
    }

    setMockMode(enabled: boolean): void {
        if (enabled) this.enableMockMode();
        else this.disableMockMode();
    }

    private enableMockMode(): void {
        if (this.mockEnabled) return;
        this.mockEnabled = true;
        useJackpotsStore.getState().stopPolling();
        this.onChange?.();
    }

    private disableMockMode(silent = false): void {
        if (!this.mockEnabled) return;
        this.mockEnabled = false;
        // Resuming real polling means the next poll replaces the whole map —
        // on QA envs the synthetic InstantLink instance disappears with it.
        // That's the intended reset behavior.
        this.instantInjected = false;
        // Win-statistics mocking rides on the frozen poll (it writes `totalWins` into it), so it
        // cannot survive polling resuming: the panel would go back to real counters while the
        // drawer still served generated rows — exactly the panel/drawer disagreement this feature
        // exists to remove. Tear it down with the same click.
        this.teardownWinsMock();
        this.stopAuto();
        // Resume real polling using the lobby's current settings. Currency
        // lives on balance, apiHost on settings.
        const state = useAppStore.getState();
        const apiHost = state.settings.apiHost;
        const currency = state.balance?.currency;
        if (apiHost && currency) {
            useJackpotsStore.getState().startPolling(apiHost, currency);
        }
        if (!silent) this.onChange?.();
    }

    /**
     * Apply `percent` to every jackpot value and push the result back into
     * the store. Returns true if anything was updated, false if no jackpots
     * are loaded yet.
     */
    triggerIncrement(percent: number): boolean {
        const jackpotsStore = useJackpotsStore.getState();
        const current = jackpotsStore.jackpotsFromAPI;
        if (!current || current.size === 0) return false;
        const mutated = applyPercentageDeep(current, percent);
        jackpotsStore.setJackpotsFromAPI(mutated);
        return true;
    }

    /**
     * Surgically set a single level's `currentValue` to `nextValue` across
     * every currency in its currency array, then push a fully-cloned Map back
     * through `setJackpotsFromAPI` so the jackpots store's diff logic fires
     * the odometer animation exactly the way a real poll would.
     *
     * Updating every currency entry (rather than only the active one) matches
     * `applyPercentageDeep`'s philosophy — the lobby may switch currencies
     * mid-test, and we want the mocked value to be visible either way.
     */
    setLevelValue(jackpotName: string, levelId: number, nextValue: number): void {
        // Auto-enable mock mode so the next real poll can't overwrite the edit.
        if (!this.mockEnabled) this.enableMockMode();

        const jackpotsStore = useJackpotsStore.getState();
        const current = jackpotsStore.jackpotsFromAPI;
        const stat = current.get(jackpotName);
        if (!stat) {
            // eslint-disable-next-line no-console
            console.warn(
                '[QA Panel] Cannot set jackpot level value —',
                jackpotName,
                'is not in jackpotsFromAPI. The jackpot may not be loaded yet.'
            );
            return;
        }

        // Immutable clone: new Map, new stat, new levelStats array, new level
        // object, new currentValue array. setJackpotsFromAPI relies on
        // reference changes for its diff logic, and the store subscribers
        // need new refs to fire.
        const levelStats = stat.levelStats || [];
        const newLevelStats = levelStats.map((level) => {
            if (level.levelId !== levelId) return { ...level };
            const entries = Array.isArray(level.currentValue) ? level.currentValue : [];
            if (entries.length === 0) {
                // No currency entries to update — acceptable no-op per edge
                // case spec. Return a fresh clone for reference hygiene.
                return { ...level };
            }
            return {
                ...level,
                currentValue: entries.map((cv) => ({
                    ...cv,
                    value: nextValue,
                })),
            };
        });

        const newStat: IJackpotStat = { ...stat, levelStats: newLevelStats };
        const newMap = new Map(current);
        newMap.set(jackpotName, newStat);
        jackpotsStore.setJackpotsFromAPI(newMap);
    }

    /**
     * Inject (or update) a synthetic InstantLink instance into the jackpots
     * store. Unlike setLevelValue this CREATES the instance when missing —
     * the InstantLink backend exists only in production, so on QA envs this
     * is the only way to light up the InstantLink UI (panel, odometer,
     * game-card bottom bands).
     *
     * Mock mode is enabled first (same discipline as setLevelValue): without
     * it the next real poll replaces the whole map and the injected instance
     * vanishes within seconds.
     */
    injectInstantJackpot(value: number): void {
        if (!this.mockEnabled) this.enableMockMode();

        const jackpotsStore = useJackpotsStore.getState();
        const current = jackpotsStore.jackpotsFromAPI;
        const existing = current.get(JACKPOT_NAMES.INSTANT_LINK);

        // Immutable clone chain — new Map, new stat, new levelStats array,
        // new level objects, new currentValue arrays — so the store's diff
        // logic fires the odometer exactly the way a real poll would.
        const newMap = new Map(current);
        if (existing) {
            const levelStats = existing.levelStats || [];
            const newLevelStats = levelStats.map((level) => ({
                ...level,
                currentValue: Array.isArray(level.currentValue)
                    ? level.currentValue.map((cv) => ({ ...cv, value }))
                    : level.currentValue,
            }));
            newMap.set(JACKPOT_NAMES.INSTANT_LINK, { ...existing, levelStats: newLevelStats });
        } else {
            newMap.set(JACKPOT_NAMES.INSTANT_LINK, buildSyntheticInstantLinkStat(value));
        }
        jackpotsStore.setJackpotsFromAPI(newMap);
        this.instantInjected = true;
    }

    /* ── Win statistics mock (statistics drawer) ──────────────── */

    /**
     * Master switch for the statistics drawer's data source.
     *
     * ON  → the drawer serves generated rows and the real `/api/jackpot/win/stats` query is
     *       DISABLED. Jackpots with no generated mock read as "no wins", which is itself a
     *       useful state: their drawer opens on the "nobody has won yet" placeholder.
     * OFF → full teardown: every generated mock is dropped and the drawer refetches for real.
     *       Destructive by design, the same way `campaignsController.disableMocking` is.
     */
    setWinsMockMode(enabled: boolean): void {
        if (enabled) {
            useJackpotStatisticsStore.getState().setWinsMockMode(true);
        } else {
            this.teardownWinsMock();
        }
        this.onChange?.();
    }

    /**
     * Generate (or regenerate) synthetic win statistics for one jackpot, auto-enabling
     * win-statistics mock mode so the drawer serves them immediately. The stats POLL is left
     * untouched (and keeps running) — the drawer reads the generated levels directly.
     *
     * @returns the total number of generated win rows across the jackpot's levels, or -1 when
     *          nothing could be generated (no games loaded yet); the bridge echoes this to the
     *          popup so it can report the failure.
     */
    generateWinStats(entry: JackpotWinsMockEntry): number {
        const stat = useJackpotsStore.getState().jackpotsFromAPI.get(entry.jackpotName);
        const levelStats = stat?.levelStats ?? [];
        const allLevelIds = levelStats
            .map((level) => level.levelId)
            .filter((levelId): levelId is number => typeof levelId === 'number');

        /*
         * Only mock the levels this jackpot ACTUALLY pays out on. Bell Link, Gods and Kings and
         * High Cash carry MINOR and MINI tiers on the panel but have no statistics for them — the
         * poll reports `totalWins: 0` and the real win-stats endpoint returns no such levels.
         * Generating for every tier would invent history the live drawer can never show.
         * If the poll reports no wins anywhere (an environment with an empty jackpot history) we
         * cannot tell which tiers are real, so fall back to the full set rather than mocking nothing.
         */
        const levelIdsWithWins = levelStats.flatMap((level) =>
            typeof level.levelId === 'number' && (level.totalWins ?? 0) > 0 ? [level.levelId] : []
        );
        const pollLevelIds = levelIdsWithWins.length > 0 ? levelIdsWithWins : allLevelIds;

        const data = buildJackpotWinsMock({
            jackpotName: entry.jackpotName,
            winsPerLevel: entry.winsPerLevel,
            includeZeroWinLevel: entry.includeZeroWinLevel,
            pollLevelIds,
            // The drawer shows every row in the JACKPOT's currency (see WIN_AMOUNT_SOURCE in
            // jackpotWins.api.ts), so mirror that rather than the player's.
            currency: stat?.currency || useAppStore.getState().balance?.currency || 'EGT',
            jackpotGameKeys: stat?.games ?? [],
        });

        if (data.levels.length === 0) return -1;

        useJackpotStatisticsStore.getState().setMockWins(entry.jackpotName, data);
        useJackpotStatisticsStore.getState().setWinsMockMode(true);
        // Store what the factory ACTUALLY did, not what was asked: the zero-win level is skipped
        // for families with fewer than three levels, and both the list label and a preset replay
        // read this back.
        const zeroWinApplied = entry.winsPerLevel > 0 && data.levels.some((level) => level.totalWins === 0);
        this.winsMocks.set(entry.jackpotName, { ...entry, includeZeroWinLevel: zeroWinApplied });

        this.onChange?.();
        return data.levels.reduce((sum, level) => sum + level.totalWins, 0);
    }

    /** Drop the generated statistics for one jackpot (or all of them when `jackpotName` is null). */
    clearWinStats(jackpotName: string | null): void {
        if (jackpotName === null) {
            this.winsMocks.clear();
            useJackpotStatisticsStore.getState().clearAllMockWins();
        } else {
            this.winsMocks.delete(jackpotName);
            useJackpotStatisticsStore.getState().clearMockWins(jackpotName);
        }
        this.onChange?.();
    }

    /**
     * Preset-export surface: the win-statistics mock as GENERATION PARAMETERS rather than as the
     * generated rows. The factory is deterministic per jackpot name, so replaying the parameters
     * reproduces the same data — and the preset JSON stays small instead of carrying hundreds of
     * win rows.
     */
    getWinsMock(): { mockMode: boolean; entries: JackpotWinsMockEntry[] } {
        return {
            mockMode: useJackpotStatisticsStore.getState().winsMockMode,
            entries: Array.from(this.winsMocks.values()),
        };
    }

    /** Clear every generated mock and flip the master switch off. */
    private teardownWinsMock(): void {
        this.winsMocks.clear();
        const statisticsStore = useJackpotStatisticsStore.getState();
        statisticsStore.clearAllMockWins();
        statisticsStore.setWinsMockMode(false);
    }

    /**
     * Preset-export surface: the injected InstantLink value, or null when
     * nothing was injected this session (a live production instance that was
     * merely frozen by mock mode is NOT exported — only QA-created mocks
     * round-trip through presets).
     */
    getInstantMock(): { value: number } | null {
        if (!this.instantInjected) return null;
        const stat = useJackpotsStore.getState().jackpotsFromAPI.get(JACKPOT_NAMES.INSTANT_LINK);
        if (!stat) return null;
        return { value: instantGrandValue(stat) };
    }

    startAuto(intervalSec: number, percent: number): void {
        this.stopAuto();
        // Clamp interval to a sane minimum so a typo can't lock the main thread.
        const clampedSec = Math.max(0.5, intervalSec);
        this.autoIntervalSec = clampedSec;
        this.autoPercent = percent;
        this.autoIntervalId = window.setInterval(() => {
            this.triggerIncrement(this.autoPercent);
        }, clampedSec * 1000);
        this.onChange?.();
    }

    stopAuto(): void {
        if (this.autoIntervalId !== null) {
            window.clearInterval(this.autoIntervalId);
            this.autoIntervalId = null;
            this.onChange?.();
        }
    }
}
